// Analysis panel inventory — indicators and charts as DATA (US-879 · R-2).
//
// Analogous to layerConfig/selectionConfig: the panel renders whatever this
// module declares, so US-880 (Digitalization Index) and US-881 (extended
// scorecard) extend the Analysis view by ADDING entries here — no panel
// rewrite. Pure data + pure accessors over the country scorecard payload
// (POST /country-data-range/, see fixtures/countryData.js for the shape);
// no React, no fetch.
//
// Every accessor returns null when its section is absent — the panel shows
// "—" or skips the chart, it never invents a value (R-7, gate resilience).
// Colors come from the theme tokens only (R-6).
import { colors, layerColors } from "../theme/tokens";

// — Pure formatters ———————————————————————————————————————————————————————————

// 1234567 → "1.2M" (en-US compact). Null-safe: non-finite → null.
export const formatCompact = (value) =>
  Number.isFinite(value)
    ? new Intl.NumberFormat("en-US", {
        notation: "compact",
        maximumFractionDigits: 1,
      }).format(value)
    : null;

// 1234567 → "$1.2M".
export const formatUsdCompact = (value) =>
  Number.isFinite(value) ? `$${formatCompact(value)}` : null;

// Backend coverage values are 0..1 fractions — display as whole percent.
export const asPercent = (fraction) =>
  Number.isFinite(fraction) ? Math.round(fraction * 100) : null;

// 1234567.4 → "1,234,567" (full integer with thousands separators — the
// detail sections show exact counts, unlike the compact scorecards).
export const formatInt = (value) =>
  Number.isFinite(value)
    ? new Intl.NumberFormat("en-US").format(Math.round(value))
    : null;

// Low–high range that collapses when both ends FORMAT the same (legacy
// OptimalTechMixTable semantics): (1e9, 1.5e9) → "$1B – $1.5B",
// (2e6, 2e6) → "2M", one end missing → the other, both missing → null.
export const formatRange = (low, high, format = formatCompact) => {
  const formattedLow = format(low);
  const formattedHigh = format(high);
  if (formattedLow == null && formattedHigh == null) return null;
  if (formattedLow == null) return formattedHigh;
  if (formattedHigh == null) return formattedLow;
  return formattedLow === formattedHigh
    ? formattedHigh
    : `${formattedLow} – ${formattedHigh}`;
};

// 300 → "300 ppl/sqkm" (the legacy "Build Fiber Out to" context line).
export const formatDensity = (value) =>
  Number.isFinite(value)
    ? `${value % 1 === 0 ? formatInt(value) : value.toFixed(1)} ppl/sqkm`
    : null;

// — Chart palette (tokens only, R-6) ——————————————————————————————————————————

export const AREA_COLORS = {
  urban: colors.navy,
  rural: layerColors.wifi,
  extremeRural: colors.gold,
};

// — Scorecards (R-2) ——————————————————————————————————————————————————————————
// Each entry: { id, label, caption?, getDisplay(data) → string|null }.
// null → the panel renders the em dash placeholder. US-880/881 add entries.

export const SCORECARDS = [
  {
    id: "population",
    label: "Population",
    caption: "World Bank estimate",
    getDisplay: (data) => formatCompact(data?.demographics?.wb_population),
  },
  {
    id: "households",
    label: "Households",
    caption: "Total across area types",
    getDisplay: (data) => formatCompact(data?.household_data?.total),
  },
  {
    id: "gni",
    label: "GNI per capita",
    caption: "World Bank, USD",
    // wb_GNI is the COUNTRY TOTAL (e.g. SGP ≈ $451B) — per capita is derived,
    // mirroring the backend's own GNI_per_capita computation (views.py).
    getDisplay: (data) => {
      const gni = data?.demographics?.wb_GNI;
      const population = data?.demographics?.wb_population;
      if (!Number.isFinite(gni) || !Number.isFinite(population) || population <= 0) {
        return null;
      }
      return formatUsdCompact(gni / population);
    },
  },
  {
    id: "capex",
    label: "Investment to close the gap",
    caption: "Last mile + backbone, lower–upper bound",
    getDisplay: (data) => {
      const lower = formatUsdCompact(data?.total_capex?.cost_lower);
      const upper = formatUsdCompact(data?.total_capex?.cost_upper);
      if (!lower || !upper) return lower || upper;
      return `${lower} – ${upper}`;
    },
  },
];

// — Charts (R-2/R-6) ——————————————————————————————————————————————————————————
// Each entry: { id, title, type: "donut"|"bar", build(data) → props|null }.
// `build` returns the full props for the matching wrapper in
// components/analysis/charts/ — or null to skip the chart (no data invented).
// US-880/881 extend by adding entries; new chart shapes add a wrapper + type.

// Shared row builder for the per-area-type bar charts.
const areaRows = (values) => {
  const rows = [
    { name: "Urban", key: "urban" },
    { name: "Rural", key: "rural" },
    { name: "Extreme rural", key: "extremeRural" },
  ]
    .map(({ name, key }) => ({ name, ...values(key) }))
    .filter((row) =>
      Object.keys(row).some((k) => k !== "name" && Number.isFinite(row[k]))
    );
  return rows.length ? rows : null;
};

// — Detail sections (US-917 · R-1..R-5) ———————————————————————————————————————
// Same contract as CHARTS: { id, title, type, build(data) → props|null }.
// Types map to section components in AnalysisPanel ("bands" → BandBreakdown,
// "matrix" → MatrixTable); `build` may also return a `caption` string. Every
// build returns null when its payload section is absent — the panel omits the
// section, it never invents values (R-4). US-880/881 add entries here.

const POPULATION_BANDS = [
  { key: "urban", label: "Urban", color: AREA_COLORS.urban },
  { key: "rural", label: "Rural", color: AREA_COLORS.rural },
  { key: "extreme_rural", label: "Extreme rural", color: AREA_COLORS.extremeRural },
];

// Population Summary (R-1): per-band Pop/HH/% of total + Total Population /
// Total Households, mirroring the legacy PopulationSummary card.
const buildPopulationSection = (data) => {
  const pop = data?.pop_data;
  const hh = data?.household_data;
  if (!pop && !hh) return null;
  const bandPop = (key) => (Number.isFinite(pop?.[key]) ? pop[key] : null);
  const bandHH = (key) => (Number.isFinite(hh?.[key]) ? hh[key] : null);
  const present = POPULATION_BANDS.map(({ key }) => bandPop(key)).filter(
    Number.isFinite
  );
  const totalPop = present.length
    ? present.reduce((sum, value) => sum + value, 0)
    : null;
  const bands = POPULATION_BANDS.map(({ key, label, color }) => {
    const populationValue = bandPop(key);
    const householdValue = bandHH(key);
    if (populationValue == null && householdValue == null) return null;
    const share =
      totalPop > 0 && populationValue != null
        ? (populationValue / totalPop) * 100
        : null;
    return {
      id: key,
      label,
      color,
      stats: [
        { label: "Pop", value: formatInt(populationValue) },
        { label: "HH", value: formatInt(householdValue) },
      ],
      bars:
        share == null
          ? []
          : [
              {
                id: "share",
                valueText: `${Math.round(share)}% of total`,
                percent: share,
                color,
              },
            ],
    };
  }).filter(Boolean);
  if (!bands.length) return null;
  return {
    bands,
    footer: [
      { label: "Total Population", value: formatInt(totalPop) },
      { label: "Total Households", value: formatInt(hh?.total) },
    ],
  };
};

// Coverage Gap (R-2): the legacy chart shows the GAP (1 − coverage), not the
// coverage. The payload only carries 0..1 coverage fractions (no *_gap /
// num_* keys — verified against the real 202), so gap percent and uncovered
// counts are derived here, client-side. Unit honesty (parity correction,
// requirements §1): fixed/satellite rows count households, but the payload's
// 4G fractions are POPULATION shares — those rows count People, not HH.
const gapFraction = (coverage) =>
  Number.isFinite(coverage) ? Math.max(0, Math.min(1, 1 - coverage)) : null;

// One gap bar row: null when the coverage fraction is absent (R-4). The
// count/denominator detail is omitted (not invented) without a denominator.
const gapRow = ({ id, label, coverage, denominator, unit, color }) => {
  const gap = gapFraction(coverage);
  if (gap == null) return null;
  const count = Number.isFinite(denominator)
    ? Math.round(gap * denominator)
    : null;
  return {
    id,
    label,
    detail:
      count != null
        ? `${formatInt(count)} / ${formatInt(denominator)} ${unit}`
        : null,
    valueText: `${asPercent(gap)}%`,
    percent: gap * 100,
    color,
  };
};

const buildCoverageGapSection = (data) => {
  const coverage = data?.coverage;
  if (!coverage) return null;
  const fixed = coverage.fixed;
  const mobile = coverage["4G"];
  const satellite = coverage.satellite;
  const hh = data?.household_data;
  const pop = data?.pop_data;

  // Urban and Rural share the same three technology rows; the fixed keys are
  // per-band lower/upper, the 4G key is the band's population fraction.
  const areaRows = (band, fixedLower, fixedUpper, mobilePop) =>
    [
      gapRow({
        id: "fiber-bottom",
        label: "Fiber (Bottom Estimate)",
        coverage: fixedLower,
        denominator: hh?.[band],
        unit: "HH",
        color: colors.neutralMid,
      }),
      gapRow({
        id: "fiber-top",
        label: "Fiber (Top Estimate)",
        coverage: fixedUpper,
        denominator: hh?.[band],
        unit: "HH",
        color: layerColors.fiber,
      }),
      gapRow({
        id: "mobile-4g",
        label: "Mobile/FWA (4G)",
        coverage: mobilePop,
        denominator: pop?.[band],
        unit: "People",
        color: layerColors.fourG,
      }),
    ].filter(Boolean);

  const bands = [
    {
      id: "urban",
      label: "Urban",
      color: AREA_COLORS.urban,
      bars: areaRows(
        "urban",
        fixed?.perc_urbanHH_lower,
        fixed?.perc_urbanHH_upper,
        mobile?.perc_urbanpop
      ),
    },
    {
      id: "rural",
      label: "Rural",
      color: AREA_COLORS.rural,
      bars: areaRows(
        "rural",
        fixed?.perc_ruralHH_lower,
        fixed?.perc_ruralHH_upper,
        mobile?.perc_ruralpop
      ),
    },
    {
      id: "extreme-rural",
      label: "Extreme rural",
      color: AREA_COLORS.extremeRural,
      bars: [
        gapRow({
          id: "satellite",
          label: "Satellite",
          coverage: satellite?.perc_extremeruralHH,
          denominator: hh?.extreme_rural,
          unit: "HH",
          color: layerColors.satellite,
        }),
      ].filter(Boolean),
    },
  ].filter((band) => band.bars.length);
  if (!bands.length) return null;
  return { bands };
};

// Cost Summary (R-3/R-4): two matrix tables over optimal_tech_mix, mirroring
// the legacy OptimalTechMixTable. Per-tech Total cells read the unsuffixed
// aggregates only the real engine emits (the stub omits them → "—", R-4); the
// TOTAL row sums whatever IS present — all inputs absent → null → "—" (the
// legacy rendered a misleading 0 there; honesty over parity).
const sumPresent = (...values) => {
  const present = values.filter(Number.isFinite);
  return present.length
    ? present.reduce((sum, value) => sum + value, 0)
    : null;
};

const MATRIX_COLUMNS = ["Urban", "Rural", "Total"];

const buildCostAnalysisSection = (data) => {
  const mix = data?.optimal_tech_mix;
  if (!mix) return null;
  const usdRange = (low, high) => formatRange(low, high, formatUsdCompact);
  const density = Number.isFinite(mix.build_fiber_to_popdensity_high)
    ? mix.build_fiber_to_popdensity_high
    : mix.optimize_popdensity;
  const densityText = formatDensity(density);
  return {
    // Context line only when the engine reported the density (stub: absent).
    caption: densityText ? `Build Fiber Out to: ${densityText}` : undefined,
    columns: MATRIX_COLUMNS,
    rows: [
      {
        id: "fiber",
        label: "Fiber",
        cells: [
          usdRange(mix.total_fiber_cost_low_urban, mix.total_fiber_cost_high_urban),
          usdRange(mix.total_fiber_cost_low_rural, mix.total_fiber_cost_high_rural),
          usdRange(mix.total_fiber_cost_low, mix.total_fiber_cost_high),
        ],
      },
      {
        id: "fwa",
        label: "FWA 4G/5G",
        cells: [
          usdRange(mix.total_fwa_cost_low_urban, mix.total_fwa_cost_high_urban),
          usdRange(mix.total_fwa_cost_low_rural, mix.total_fwa_cost_high_rural),
          usdRange(mix.total_fwa_cost_low, mix.total_fwa_cost_high),
        ],
      },
      {
        id: "satellite",
        label: "Satellite",
        cells: [
          null,
          null,
          usdRange(mix.total_satellite_cost_low, mix.total_satellite_cost_high),
        ],
      },
    ],
    totalRow: {
      label: "TOTAL",
      cells: [
        usdRange(
          sumPresent(mix.total_fiber_cost_low_urban, mix.total_fwa_cost_low_urban),
          sumPresent(mix.total_fiber_cost_high_urban, mix.total_fwa_cost_high_urban)
        ),
        usdRange(
          sumPresent(mix.total_fiber_cost_low_rural, mix.total_fwa_cost_low_rural),
          sumPresent(mix.total_fiber_cost_high_rural, mix.total_fwa_cost_high_rural)
        ),
        usdRange(
          sumPresent(
            mix.total_fiber_cost_low,
            mix.total_fwa_cost_low,
            mix.total_satellite_cost_low
          ),
          sumPresent(
            mix.total_fiber_cost_high,
            mix.total_fwa_cost_high,
            mix.total_satellite_cost_high
          )
        ),
      ],
    },
  };
};

const buildHouseholdCoverageSection = (data) => {
  const mix = data?.optimal_tech_mix;
  if (!mix) return null;
  const hhRange = (low, high) => formatRange(low, high, formatCompact);
  // FWA and satellite HH figures are single values (real engine only, R-4);
  // legacy semantics: a single value counts as both range ends in sums.
  const fwaUrban = Number.isFinite(mix.total_hh_covered_fwa_urban)
    ? mix.total_hh_covered_fwa_urban
    : null;
  const fwaRural = Number.isFinite(mix.total_hh_covered_fwa_rural)
    ? mix.total_hh_covered_fwa_rural
    : null;
  const fwaTotal = sumPresent(fwaUrban, fwaRural);
  const satelliteTotal = Number.isFinite(mix.total_hh_covered_satellite)
    ? mix.total_hh_covered_satellite
    : null;
  return {
    columns: MATRIX_COLUMNS,
    rows: [
      {
        id: "fiber",
        label: "Fiber",
        cells: [
          hhRange(
            mix.total_hh_covered_fiber_low_urban,
            mix.total_hh_covered_fiber_high_urban
          ),
          hhRange(
            mix.total_hh_covered_fiber_low_rural,
            mix.total_hh_covered_fiber_high_rural
          ),
          hhRange(mix.total_hh_covered_fiber_low, mix.total_hh_covered_fiber_high),
        ],
      },
      {
        id: "fwa",
        label: "FWA 4G/5G",
        cells: [formatCompact(fwaUrban), formatCompact(fwaRural), formatCompact(fwaTotal)],
      },
      {
        id: "satellite",
        label: "Satellite",
        cells: [null, null, formatCompact(satelliteTotal)],
      },
    ],
    totalRow: {
      label: "TOTAL",
      cells: [
        hhRange(
          sumPresent(mix.total_hh_covered_fiber_low_urban, fwaUrban),
          sumPresent(mix.total_hh_covered_fiber_high_urban, fwaUrban)
        ),
        hhRange(
          sumPresent(mix.total_hh_covered_fiber_low_rural, fwaRural),
          sumPresent(mix.total_hh_covered_fiber_high_rural, fwaRural)
        ),
        hhRange(
          sumPresent(mix.total_hh_covered_fiber_low, fwaTotal, satelliteTotal),
          sumPresent(mix.total_hh_covered_fiber_high, fwaTotal, satelliteTotal)
        ),
      ],
    },
  };
};

export const DETAIL_SECTIONS = [
  {
    id: "population-detail",
    title: "Population Summary",
    type: "bands",
    build: buildPopulationSection,
  },
  {
    id: "coverage-gap",
    title: "Coverage Gap",
    type: "bands",
    build: buildCoverageGapSection,
  },
  {
    id: "cost-analysis",
    title: "Cost Analysis",
    type: "matrix",
    build: buildCostAnalysisSection,
  },
  {
    id: "household-coverage",
    title: "Household Coverage",
    type: "matrix",
    build: buildHouseholdCoverageSection,
  },
];

export const CHARTS = [
  {
    id: "population-mix",
    title: "Population by area type",
    type: "donut",
    build: (data) => {
      const pop = data?.pop_data;
      if (!pop) return null;
      const rows = [
        { name: "Urban", value: pop.urban, color: AREA_COLORS.urban },
        { name: "Rural", value: pop.rural, color: AREA_COLORS.rural },
        {
          name: "Extreme rural",
          value: pop.extreme_rural,
          color: AREA_COLORS.extremeRural,
        },
      ].filter((row) => Number.isFinite(row.value) && row.value > 0);
      if (!rows.length) return null;
      return { data: rows, valueFormatter: formatCompact };
    },
  },
  {
    id: "coverage-4g",
    title: "4G population coverage",
    type: "bar",
    build: (data) => {
      const cov = data?.coverage?.["4G"];
      if (!cov) return null;
      const rows = areaRows((key) => ({
        covered: asPercent(
          {
            urban: cov.perc_urbanpop,
            rural: cov.perc_ruralpop,
            extremeRural: cov.perc_extreme_ruralpop,
          }[key]
        ),
      }));
      if (!rows) return null;
      return {
        data: rows,
        bars: [{ dataKey: "covered", label: "Covered", color: layerColors.fourG }],
        unit: "%",
        max: 100,
      };
    },
  },
  {
    id: "coverage-fixed",
    title: "Fixed coverage of households",
    type: "bar",
    build: (data) => {
      const fixed = data?.coverage?.fixed;
      if (!fixed) return null;
      const rows = areaRows((key) => ({
        lower: asPercent(
          {
            urban: fixed.perc_urbanHH_lower,
            rural: fixed.perc_ruralHH_lower,
            extremeRural: fixed.perc_extreme_ruralHH_lower,
          }[key]
        ),
        upper: asPercent(
          {
            urban: fixed.perc_urbanHH_upper,
            rural: fixed.perc_ruralHH_upper,
            extremeRural: fixed.perc_extreme_ruralHH_upper,
          }[key]
        ),
      }));
      if (!rows) return null;
      return {
        data: rows,
        bars: [
          { dataKey: "lower", label: "Lower bound", color: colors.neutralMid },
          { dataKey: "upper", label: "Upper bound", color: layerColors.fiber },
        ],
        unit: "%",
        max: 100,
      };
    },
  },
];
